import { useEffect, useRef } from "react";
import {
  CandlestickSeries,
  createChart,
  createSeriesMarkers,
  type CandlestickData,
  type SeriesMarker,
  type UTCTimestamp,
} from "lightweight-charts";

import type { components } from "../../api/schema";
import { isOpeningAction } from "../../lib/tradeAnalytics";
import { formatFillAction, formatQuantity } from "../../lib/tradeFormat";
import {
  CHART_HEIGHT_PX,
  getChartColors,
  getCommonChartOptions,
} from "../charts/chartOptions";
import { useTheme } from "../theme/ThemeProvider";

type ChartBar = components["schemas"]["ChartBarResponse"];
type FillResponse = components["schemas"]["FillResponse"];

export interface CandlestickChartProps {
  bars: ReadonlyArray<ChartBar>;
  fills: ReadonlyArray<FillResponse>;
  // Render height in px. Defaults to the compact CHART_HEIGHT_PX the preview
  // surfaces use; the trade-detail Chart tab passes a taller value so the
  // candles read clearly instead of as a letterbox strip.
  height?: number;
}

function toSeconds(iso: string): UTCTimestamp {
  return Math.floor(new Date(iso).getTime() / 1000) as UTCTimestamp;
}

// Intraday candlestick chart of OHLC bars (massive.com via the chart endpoint)
// with the trade's executions overlaid as entry/exit markers. Supersedes the
// FillTimelineChart execution-price line on the trade-detail Chart tab
// (ADR 0033 U5). Renders an empty state when the bar list is empty -- the
// endpoint returns no bars for a window the vendor doesn't cover.
export function CandlestickChart({
  bars,
  fills,
  height = CHART_HEIGHT_PX,
}: CandlestickChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const container = containerRef.current;
    if (container === null || bars.length === 0) {
      return;
    }
    const colors = getChartColors();
    const chart = createChart(container, {
      ...getCommonChartOptions(),
      height,
      width: container.clientWidth,
      // Intraday bars: show the time of day on the axis + crosshair, not just
      // the date (otherwise every label collapses to the day-of-month).
      timeScale: { timeVisible: true, secondsVisible: false },
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: colors.positive,
      downColor: colors.negative,
      borderUpColor: colors.positive,
      borderDownColor: colors.negative,
      wickUpColor: colors.positive,
      wickDownColor: colors.negative,
    });

    const candles: CandlestickData<UTCTimestamp>[] = bars
      .map((bar) => ({
        time: toSeconds(bar.timestamp),
        open: Number(bar.open),
        high: Number(bar.high),
        low: Number(bar.low),
        close: Number(bar.close),
      }))
      .sort((a, b) => a.time - b.time);
    series.setData(candles);

    const markers: SeriesMarker<UTCTimestamp>[] = [...fills]
      .sort(
        (a, b) =>
          new Date(a.executed_at).getTime() - new Date(b.executed_at).getTime(),
      )
      .map((fill) => {
        const opening = isOpeningAction(fill.action);
        return {
          time: toSeconds(fill.executed_at),
          position: opening ? "belowBar" : "aboveBar",
          color: opening ? colors.positive : colors.negative,
          shape: opening ? "arrowUp" : "arrowDown",
          text: `${formatFillAction(fill.action)} ${formatQuantity(fill.quantity)}`,
        };
      });
    if (markers.length > 0) {
      createSeriesMarkers(series, markers);
    }

    chart.timeScale().fitContent();

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        chart.applyOptions({ width: entry.contentRect.width });
      }
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      chart.remove();
    };
  }, [bars, fills, theme, height]);

  return (
    <div
      className="relative w-full"
      style={{ height }}
      data-testid="candlestick-chart"
    >
      <div ref={containerRef} className="h-full w-full" />
      {bars.length === 0 && (
        <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-on-surface-muted">
          No price data for this period
        </p>
      )}
    </div>
  );
}
